import { useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Plus } from "lucide-react";
import { SERVICES, SHOOT_EXTRAS, waLink } from "@/data/site";
import MasonryGallery from "@/components/MasonryGallery";
import CTASection from "@/components/CTASection";
import PackagesGrid from "@/components/PackagesGrid";
import ComboSection from "@/components/ComboSection";
import { SERVICE } from "@/constants/testIds";

const SHOOT_SLUGS = ["huwelijk", "zwangerschap", "newborn", "gezin", "communie"];

const Service = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const s = SERVICES[slug];
  if (!s) return <Navigate to="/" replace />;

  return (
    <>
      {/* Hero */}
      <section className="relative h-[80vh] overflow-hidden">
        <img
          src={s.heroImage}
          alt={s.title}
          className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28 container-x">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="eyebrow text-gold mb-4"
          >
            {s.tagline}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="font-serif text-white text-6xl md:text-7xl lg:text-8xl leading-[1.02]"
          >
            <span className="italic font-light">{s.title}</span>
          </motion.h1>
        </div>
      </section>

      {/* Intro + Highlights */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-narrow grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="font-serif text-2xl md:text-3xl leading-snug text-ink">{s.intro}</p>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm text-neutral-700">
                  <Check size={16} className="text-gold mt-1 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="md:col-span-5 bg-beige p-8 md:p-10">
            <p className="eyebrow mb-3">Vanaf</p>
            <p className="font-serif text-4xl md:text-5xl mb-2">{s.priceFrom}</p>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Elk pakket wordt op maat samengesteld. Contacteer me voor een persoonlijke offerte.
            </p>
            <a
              data-testid={SERVICE.bookBtn}
              href={waLink(`Hallo Eric, ik wil graag meer info over de ${s.title.toLowerCase()} fotoshoot.`)}
              target="_blank"
              rel="noreferrer"
              className="btn-gold w-full mt-8"
            >
              Plan jouw fotoshoot
            </a>
            <Link
              to="/contact"
              className="block text-center text-xs uppercase tracking-[0.25em] mt-4 text-neutral-600 hover:text-gold transition"
            >
              of stuur een bericht
            </Link>
          </aside>
        </div>
      </section>

      {/* Subcategories (used by /gezin) */}
      {s.subcategories?.length > 0 && (
        <section className="bg-white py-24 md:py-32">
          <div className="container-x">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <p className="eyebrow mb-3 flex items-center justify-center gap-3">
                <span className="text-gold font-script text-2xl leading-none">♡</span>
                {s.subcategoriesTitle || "Kies je shoot"}
                <span className="text-gold font-script text-2xl leading-none">♡</span>
              </p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Elk moment <span className="italic text-gold">verdient</span> zijn eigen taal
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {s.subcategories.map((sub, i) => (
                <motion.article
                  key={sub.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className={`relative flex flex-col ${sub.highlight ? "ring-2 ring-gold shadow-2xl" : "border border-neutral-200"}`}
                >
                  {sub.badge && (
                    <span className="absolute -top-3 left-6 z-10 text-[10px] uppercase tracking-[0.3em] text-white bg-gold px-3 py-1">
                      {sub.badge}
                    </span>
                  )}
                  <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                    <img
                      src={sub.image}
                      alt={sub.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-3 right-3 font-script text-gold text-xl">♡</span>
                  </div>
                  <div className={`p-6 md:p-8 flex-1 flex flex-col ${sub.highlight ? "bg-beige" : "bg-white"}`}>
                    <h3 className="font-serif text-2xl tracking-wide">{sub.title}</h3>
                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{sub.desc}</p>
                    <div className="mt-6 flex flex-wrap gap-3 mt-auto pt-6">
                      <a
                        data-testid={`sub-cta-${sub.title.toLowerCase().replace(/\s+/g,'-')}`}
                        href={waLink(sub.message || `Hallo Eric, ik wil graag info over ${sub.title.toLowerCase()}.`)}
                        target="_blank"
                        rel="noreferrer"
                        className={`inline-flex px-5 py-3 text-[10px] uppercase tracking-[0.3em] transition-colors ${
                          sub.highlight
                            ? "bg-gold text-white hover:bg-gold-dark"
                            : "border border-ink text-ink hover:bg-ink hover:text-white"
                        }`}
                      >
                        {sub.cta || "Boek nu"}
                      </a>
                      {sub.linkTo && (
                        <Link
                          to={sub.linkTo}
                          className="inline-flex items-center px-5 py-3 text-[10px] uppercase tracking-[0.3em] border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold transition-colors"
                        >
                          Meer info →
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packages */}
      {s.packages?.length > 0 && (
        <section className="bg-beige py-24 md:py-32">
          <div className="container-x">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="eyebrow mb-3">Pakketten</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Kies wat past bij <span className="italic text-gold">jouw verhaal</span>
              </h2>
            </div>
            <PackagesGrid packages={s.packages} serviceName={s.title} />

            {SHOOT_SLUGS.includes(slug) && (
              <div data-testid="shoot-extras" className="max-w-2xl mx-auto mt-16 text-center border-t border-gold/30 pt-12">
                <p className="eyebrow mb-4 text-gold">Extra&apos;s (optioneel)</p>
                <ul className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
                  {SHOOT_EXTRAS.map((e) => (
                    <li key={e} className="flex items-center gap-2 text-sm text-neutral-700">
                      <Plus size={15} className="text-gold shrink-0" />
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-neutral-500 italic">
                  Bij elke shoot bij te bestellen — vraag gerust naar de mogelijkheden.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Combos related to this service */}
      {s.combos?.length > 0 && (
        <ComboSection
          ids={s.combos}
          title="Meer beleving, meer voordeel"
        />
      )}

      {/* Gallery */}
      <section className="bg-white pt-16 md:pt-24 pb-4">
        <div className="container-x text-center">
          <p className="eyebrow mb-3">Portfolio</p>
          <h2 className="font-serif text-4xl md:text-5xl">
            Werk uit deze <span className="italic text-gold">categorie</span>
          </h2>
        </div>
      </section>
      <MasonryGallery filterable={false} initialTag={s.galleryTag} />

      <CTASection />
    </>
  );
};

export default Service;
